import { useMemo } from 'react';
import React, { useCallback, useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import type { GooglePlacesAddress } from '@sweetgreen/garnish';
import { formatGoogleAddress } from '@sweetgreen/garnish';
import {
  convertGooglePlacesAddress,
  SPACING,
  theme,
  usePlacesAutocomplete,
  useResponsive,
} from '@sweetgreen/garnish';

import { getEnvVars } from '@order/utils';

import { AddressSearchField, AddressSearchResults } from '../../AddressSearch';
import type { AddressForm, AutoCompleteParams } from '../AddressForm.types';

export const AddressFormPredictions = ({
  placeId,
  address,
  visible,
  disabled,
  setPlaceId,
  setValue,
}: AddressFormPredictionsProps) => {
  // ─── Auto Complete State ───────────────────────────────────
  const {
    loading,
    error,
    noResults,
    searchString,
    predictions,
    setSearchString,
    clearAutoCompleteResults,
  } = usePlacesAutocomplete({ apiKey: getEnvVars().GOOGLE_API_KEY });

  // ─── Memoized Results ──────────────────────────────────────
  const predictionsData = useMemo(() => {
    return { predictions, loading, error, noResults };
  }, [error, loading, noResults, predictions]);

  // ─── Load Initial Address ──────────────────────────────────
  useInitialAddress({
    visible,
    address,
    setSearchString,
    setPlaceId,
  });

  // ─── Load Results ──────────────────────────────────────────
  useGoogleAutocompleteResults({
    placeId,
    setValue,
    setSearchString,
    clearAutoCompleteResults,
  });

  // ─── Callbacks ─────────────────────────────────────────────
  const handleChangeText = useCallback(
    (input: string) => {
      setPlaceId('');
      setSearchString(input);
    },
    [setPlaceId, setSearchString],
  );

  // ─── Responsiveness ────────────────────────────────────────
  const { minWidth } = useResponsive();
  const autoCompleteResultContentStyle = minWidth.tablet
    ? styles.autocompleteResultContentTablet
    : styles.autocompleteResultContentMobile;

  // ─── Result Checks ─────────────────────────────────────────
  const hasPlaceId = Boolean(placeId);
  const hasEnteredMinChars = searchString.length >= 3;
  const isLoadingOrReady = loading || !noResults;
  const shouldShowPredictions =
    hasEnteredMinChars && !hasPlaceId && isLoadingOrReady;

  return (
    <View style={styles.autoCompleteWrapper}>
      <AddressSearchField
        isRequired
        disabled={disabled}
        value={searchString}
        hasResults={!noResults}
        onChangeText={handleChangeText}
        onSubmit={setSearchString}
      />

      {shouldShowPredictions ? (
        <View style={styles.autocompleteResult}>
          <AddressSearchResults
            predictionsData={predictionsData}
            onPress={setPlaceId}
            useDesktopStyles
            style={autoCompleteResultContentStyle}
          />
        </View>
      ) : null}
    </View>
  );
};

// ─── Hooks ───────────────────────────────────────────────────

const useInitialAddress = ({
  visible,
  address,
  setSearchString,
  setPlaceId,
}: UseInitialAddressProps) => {
  useEffect(() => {
    if (!visible || !address) return;
    const searchString = formatGoogleAddress(address);

    setSearchString(searchString ?? '');
    setPlaceId(address.googlePlaceId);
  }, [address, visible, setSearchString, setPlaceId]);
};

const useGoogleAutocompleteResults = ({
  placeId,
  setSearchString,
  clearAutoCompleteResults,
  setValue,
}: AutoCompleteParams) => {
  useEffect(() => {
    if (!placeId) return;
    (async () => {
      const { formattedAddress, ...newAddress } =
        await convertGooglePlacesAddress({
          placeId,
          apiKey: getEnvVars().GOOGLE_API_KEY,
        });

      clearAutoCompleteResults();
      setSearchString(formattedAddress ?? '');
      setValue('latitude', newAddress.latitude);
      setValue('longitude', newAddress.longitude);
      setValue('street', newAddress.street);
      setValue('zipCode', newAddress.zipCode);
      setValue('city', newAddress.city);
      setValue('state', newAddress.state);
      setValue('country', newAddress.country);
      setValue('googlePlaceId', newAddress.googlePlaceId, {
        shouldDirty: true,
      });
    })();
  }, [placeId, setValue, clearAutoCompleteResults, setSearchString]);
};

// ─── Types ───────────────────────────────────────────────────

type AddressFormPredictionsProps = {
  readonly placeId: string;
  readonly address: GooglePlacesAddress;
  readonly visible?: boolean;
  readonly disabled?: boolean;
  readonly setPlaceId: (placeId: string) => void;
  readonly setValue: UseFormSetValue<AddressForm>;
};

type UseInitialAddressProps = {
  readonly visible?: boolean;
  readonly address?: GooglePlacesAddress;
  readonly setSearchString: (searchString: string) => void;
  readonly setPlaceId: (placeId: string) => void;
};

// ─── Styles ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  autoCompleteWrapper: {
    zIndex: theme.zIndex.dropdown,
    paddingBottom: theme.spacing.medium,
  },
  autocompleteResult: {
    position: 'absolute',
    top: theme.spacing.xlarge,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.OATMEAL,
    borderBottomColor: theme.colors.NEUTRAL_4,
    borderBottomWidth: 1,
  },
  autocompleteResultContentMobile: {
    paddingHorizontal: SPACING.medium,
  },
  autocompleteResultContentTablet: {
    paddingHorizontal: SPACING.large,
  },
});
